// Invite a café (spec/14 §3.1, F-HQ-02, ADR-0046): `POST /v1/hq/organizations`, the contract's
// `CreateHqOrganization`. Head office gives the café's business name and its owner's email or phone, and
// nothing else is required: the owner creates their account from the invitation and completes the legal
// name, PAN, registered address and their first venue in a short guided setup. Anything head office
// already knows goes under "More details (optional)" — the slug (written from the name until HQ writes
// its own), the legal name, the kind (`independent` unless HQ says otherwise), the PAN, the registered
// address and the HQ access level (`full` unless an agreement limits it).
//
// The form checks only what a person can fix before sending — a name or an owner left empty, an address
// or a number that is not one, an address begun and left half-written; the api checks everything again.
// A `400` puts each field error under the field it names, in the contract's words, opening "More details"
// when the field is in there; `409 slug-taken` goes under the slug. Nothing is kept on a failure but the
// form: a retried invitation never makes a second organisation, because the slug is taken by the first.
//
// Plan, subscription and network membership are not asked: they come with their features (F-HQ-07,
// F-HQ-08, F-HQ-05). GST registration is not asked either: it is per venue (spec/06 §10.3).

import type { Address, CreateHqOrganization, HqAccess, OrganizationKind } from '@clavian/api-client';
// The slug writer alone, not the whole of `@clavian/domain`: the HQ build carries only what it uses.
import { organizationSlugFrom } from '@clavian/domain/organization-slug';
import { countries } from '@clavian/i18n';
import { useTranslation } from '@clavian/i18n/react';
import { Button, RadioGroup, SelectField, TextField } from '@clavian/ui-web';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Controller, useForm, useWatch, type Control, type FieldPath, type UseFormGetValues } from 'react-hook-form';

import { useApi } from '../../api/client';
import { problemCode, PROBLEM } from '../../api/problems';
import { useProblem } from '../../api/use-problem';
import { usePhoneSignIn } from '../../auth/sign-in-methods';
import { DEFAULT_COUNTRY } from '../../i18n';
import { LoadFailure } from '../../shell/LoadFailure';
import { RequirePlatformPermission } from '../access';
import { HQ_PATHS } from '../paths';
import { HQ_ORGANIZATIONS } from '../sections';
import { EMPTY_OWNER, ownerContactOf, OwnerContactFields, placeFieldErrors, type OwnerContactValues } from './owner';
import { rememberCreated } from './queries';
import { HqHeading } from '../heading';

// The top-level kinds HQ founds directly (ADR-0019): a brand (`franchisor`) or a standalone owner
// (`independent`). A `franchisee` is created under its franchisor, with the franchisor link, in the
// franchise flow (Phase 2) — not from this form, which founds a top-level organisation.
const KINDS: readonly OrganizationKind[] = ['independent', 'franchisor'];
const ACCESS_LEVELS: readonly HqAccess[] = ['full', 'aggregate_only'];

interface AddressValues {
  readonly line1: string;
  readonly line2: string;
  readonly landmark: string;
  readonly locality: string;
  readonly region: string;
  readonly postalCode: string;
  readonly country: string;
}

export interface NewOrganizationValues {
  readonly name: string;
  readonly slug: string;
  readonly legalName: string;
  readonly kind: OrganizationKind;
  readonly pan: string;
  readonly registeredAddress: AddressValues;
  readonly hqAccess: HqAccess;
  readonly owner: OwnerContactValues;
}

type FormPath = FieldPath<NewOrganizationValues>;

const EMPTY: NewOrganizationValues = {
  name: '',
  slug: '',
  legalName: '',
  kind: 'independent',
  pan: '',
  registeredAddress: { line1: '', line2: '', landmark: '', locality: '', region: '', postalCode: '', country: DEFAULT_COUNTRY },
  hqAccess: 'full',
  owner: EMPTY_OWNER,
};

/** The api's field paths that this form has a field for, and that field. */
const API_FIELDS: Readonly<Record<string, FormPath>> = {
  name: 'name',
  slug: 'slug',
  legalName: 'legalName',
  kind: 'kind',
  pan: 'pan',
  hqAccess: 'hqAccess',
  'registeredAddress.line1': 'registeredAddress.line1',
  'registeredAddress.line2': 'registeredAddress.line2',
  'registeredAddress.landmark': 'registeredAddress.landmark',
  'registeredAddress.locality': 'registeredAddress.locality',
  'registeredAddress.region': 'registeredAddress.region',
  'registeredAddress.postalCode': 'registeredAddress.postalCode',
  'registeredAddress.country': 'registeredAddress.country',
  'owner.email': 'owner.email',
  'owner.phoneNumber': 'owner.phoneNumber',
};

/** The fields under "More details": a refusal that names one opens it, so the field can be seen and fixed. */
const DETAIL_FIELDS: ReadonlySet<string> = new Set(['slug', 'legalName', 'kind', 'pan', 'hqAccess', 'registeredAddress']);
const isDetailField = (path: string): boolean => DETAIL_FIELDS.has(path.split('.')[0] ?? '');

/** A slug from a trade name, as the contract writes one: `Playground Gaming` → `playground-gaming`. */
export const slugFrom = organizationSlugFrom;

/** The parts of an address that say one has been begun: then the lines a postal address needs are asked for. */
function addressBegun(address: AddressValues): boolean {
  return [address.line1, address.line2, address.landmark, address.locality, address.region, address.postalCode].some((part) => part.trim() !== '');
}

/** The request, from what was typed: trimmed, the PAN in capitals, an optional field left out when empty. */
export function createBodyOf(values: NewOrganizationValues): CreateHqOrganization {
  const address = values.registeredAddress;
  const optional = (value: string) => value.trim();
  const registeredAddress: Address | undefined = addressBegun(address)
    ? {
        line1: address.line1.trim(),
        ...(optional(address.line2) === '' ? {} : { line2: optional(address.line2) }),
        ...(optional(address.landmark) === '' ? {} : { landmark: optional(address.landmark) }),
        locality: address.locality.trim(),
        region: address.region.trim(),
        postalCode: address.postalCode.trim(),
        country: address.country,
      }
    : undefined;
  const pan = values.pan.trim().toUpperCase();
  const legalName = values.legalName.trim();
  // The slug HQ wrote, or the one the name makes; none at all when the name makes none, for the api to say so.
  const slug = values.slug.trim() !== '' ? values.slug.trim() : organizationSlugFrom(values.name);
  return {
    name: values.name.trim(),
    ...(slug === '' ? {} : { slug }),
    ...(legalName === '' ? {} : { legalName }),
    kind: values.kind,
    ...(pan === '' ? {} : { pan }),
    ...(registeredAddress === undefined ? {} : { registeredAddress }),
    hqAccess: values.hqAccess,
    owner: ownerContactOf(values.owner),
  };
}

type Failure = { readonly kind: 'refused'; readonly messages: readonly string[] } | { readonly kind: 'forbidden' } | { readonly kind: 'error'; readonly error: unknown };

export function NewOrganizationPage(): ReactNode {
  return (
    <RequirePlatformPermission permission={HQ_ORGANIZATIONS}>
      <InviteCafeForm />
    </RequirePlatformPermission>
  );
}

function InviteCafeForm() {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const phoneOn = usePhoneSignIn();
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const form = useForm<NewOrganizationValues>({ defaultValues: EMPTY });
  const [failure, setFailure] = useState<Failure | undefined>(undefined);
  const [moreOpen, setMoreOpen] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  // The slug follows the business name until someone writes one of their own.
  const slugEdited = useRef(false);
  const channel = useWatch({ control: form.control, name: 'owner.channel' });

  const create = useMutation({ mutationFn: (body: CreateHqOrganization) => api.createHqOrganization({ body }) });

  // After a refusal, take the reader to the first field it names — "More details" is open by now if it is in there.
  useEffect(() => {
    if (failure?.kind !== 'refused') return;
    formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
  }, [failure]);

  const submit = form.handleSubmit(
    async (values) => {
      setFailure(undefined);
      try {
        const created = await create.mutateAsync(createBodyOf(values));
        await rememberCreated(queryClient, created.organization, created.ownerInvitation);
        const params: Readonly<Record<string, string>> = { organizationId: created.organization.id };
        await navigate({ to: HQ_PATHS.organization, params });
      } catch (error) {
        const placed: string[] = [];
        const place = (name: string, message: string) => {
          placed.push(name);
          form.setError(name as FormPath, { type: 'server', message });
        };
        if (problemCode(error) === PROBLEM.slugTaken) {
          // The slug the name made, now visible to change.
          if (form.getValues('slug').trim() === '') form.setValue('slug', organizationSlugFrom(values.name));
          place('slug', t('organizations.new.slugTaken'));
          setMoreOpen(true);
          setFailure({ kind: 'refused', messages: [] });
        } else if (problemCode(error) === PROBLEM.validationFailed) {
          const messages = placeFieldErrors(error, API_FIELDS, place);
          if (placed.some(isDetailField)) setMoreOpen(true);
          setFailure({ kind: 'refused', messages });
        } else if (problemCode(error) === PROBLEM.phoneInvitationsOff) {
          // Phone sign-in was turned off since the form was drawn: the owner is invited by email.
          setFailure({ kind: 'refused', messages: [problems.text(error, { fallback: t('organizations.owner.phoneOff') })] });
        } else if (problemCode(error) === PROBLEM.forbidden) {
          setFailure({ kind: 'forbidden' });
        } else {
          setFailure({ kind: 'error', error });
        }
      }
    },
    (errors) => {
      if (Object.keys(errors).some(isDetailField)) setMoreOpen(true);
      setFailure({ kind: 'refused', messages: [] });
    },
  );

  return (
    <div className="console-page">
      <div className="console-hq-titlebar">
        <div>
          <Link className="console-hq-back" to={HQ_PATHS.organizations}>
            {t('organizations.new.back')}
          </Link>
          <HqHeading title={t('organizations.new.title')} tip={t('organizations.new.intro')} />
        </div>
      </div>

      <form ref={formRef} className="console-hq-form" noValidate aria-label={t('organizations.new.title')} onSubmit={(event) => void submit(event)}>
        <FailureNotice failure={failure} />

        <fieldset className="console-hq-fieldset">
          <legend>{t('organizations.new.section.cafe')}</legend>
          <Text
            control={form.control}
            name="name"
            label={t('organizations.new.field.name')}
            description={t('organizations.new.hint.name')}
            required
            onValue={(value) => {
              if (!slugEdited.current) form.setValue('slug', organizationSlugFrom(value));
            }}
          />
        </fieldset>

        <fieldset className="console-hq-fieldset">
          <legend>{t('organizations.new.section.owner')}</legend>
          <p className="console-muted">{t(phoneOn ? 'organizations.new.hint.owner' : 'organizations.new.hint.ownerEmail')}</p>
          <OwnerContactFields
            control={form.control}
            names={{ channel: 'owner.channel', email: 'owner.email', phoneNumber: 'owner.phoneNumber' }}
            channel={channel}
          />
        </fieldset>

        <details className="console-hq-details" open={moreOpen} onToggle={(event) => setMoreOpen(event.currentTarget.open)}>
          <summary>{t('organizations.new.section.more')}</summary>
          <div className="console-hq-details__body">
            <p className="console-muted">{t('organizations.new.hint.more')}</p>
            <Text
              control={form.control}
              name="slug"
              label={t('organizations.new.field.slug')}
              description={t('organizations.new.hint.slug')}
              spellCheck={false}
              onValue={() => {
                slugEdited.current = true;
              }}
            />
            <Text
              control={form.control}
              name="legalName"
              label={t('organizations.new.field.legalName')}
              description={t('organizations.new.hint.legalName')}
            />
            <Controller
              control={form.control}
              name="kind"
              render={({ field, fieldState }) => (
                <RadioGroup
                  label={t('organizations.new.field.kind')}
                  name={field.name}
                  value={field.value}
                  onValueChange={field.onChange}
                  error={fieldState.error?.message}
                  options={KINDS.map((kind) => ({
                    value: kind,
                    label: (
                      <>
                        {t(`organizations.kind.${kind}`)} <span className="console-muted">{t(`organizations.kindHint.${kind}`)}</span>
                      </>
                    ),
                  }))}
                />
              )}
            />
            <Text
              control={form.control}
              name="pan"
              label={t('organizations.new.field.pan')}
              description={t('organizations.new.hint.pan')}
              spellCheck={false}
              autoCapitalize="characters"
            />
            <AddressFields control={form.control} getValues={form.getValues} />
            <Controller
              control={form.control}
              name="hqAccess"
              render={({ field, fieldState }) => (
                <RadioGroup
                  label={t('organizations.new.field.hqAccess')}
                  description={t('organizations.new.hint.hqAccess')}
                  name={field.name}
                  value={field.value}
                  onValueChange={field.onChange}
                  error={fieldState.error?.message}
                  options={ACCESS_LEVELS.map((level) => ({
                    value: level,
                    label: (
                      <>
                        {t(`organizations.hqAccess.${level}`)} <span className="console-muted">{t(`organizations.hqAccessHint.${level}`)}</span>
                      </>
                    ),
                  }))}
                />
              )}
            />
          </div>
        </details>

        <div className="console-form__actions">
          <Button type="submit" variant="primary" busy={form.formState.isSubmitting}>
            {t('organizations.new.submit')}
          </Button>
          <Button asChild variant="ghost">
            <Link to={HQ_PATHS.organizations}>{t('organizations.new.cancel')}</Link>
          </Button>
        </div>
      </form>
    </div>
  );
}

function FailureNotice({ failure }: { readonly failure: Failure | undefined }) {
  const { t } = useTranslation('hq');
  if (failure === undefined) return null;
  if (failure.kind === 'error') return <LoadFailure error={failure.error} />;
  return (
    <div className="console-alert" role="alert">
      <p>{failure.kind === 'forbidden' ? t('organizations.new.forbidden') : t('organizations.new.refused')}</p>
      {failure.kind === 'refused' && failure.messages.length > 0 ? (
        <ul>
          {failure.messages.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * The registered address, optional as a whole: left empty, the owner completes it during setup. Once one
 * part of it is written, the first line, the city, the state and the PIN code are asked for, so a
 * half-written address is never sent.
 */
function AddressFields({ control, getValues }: { readonly control: Control<NewOrganizationValues>; readonly getValues: UseFormGetValues<NewOrganizationValues> }) {
  const { t, i18n } = useTranslation('hq');
  const label = (field: string) => t(`console:venueProfile.label.${field}`);
  const options = useMemo(() => countries(i18n.language).map((country) => ({ value: country.code, label: country.name })), [i18n.language]);
  const neededOnceBegun = () => addressBegun(getValues('registeredAddress'));
  return (
    <fieldset className="console-hq-fieldset console-hq-fieldset--inner">
      <legend>{t('organizations.new.section.address')}</legend>
      <p className="console-muted">{t('organizations.new.hint.address')}</p>
      <Text control={control} name="registeredAddress.line1" label={label('line1')} requiredWhen={neededOnceBegun} autoComplete="off" />
      <Text control={control} name="registeredAddress.line2" label={label('line2')} autoComplete="off" />
      <Text control={control} name="registeredAddress.landmark" label={label('landmark')} autoComplete="off" />
      <div className="console-hq-pair">
        <Text control={control} name="registeredAddress.locality" label={label('locality')} requiredWhen={neededOnceBegun} autoComplete="off" />
        <Text control={control} name="registeredAddress.region" label={label('region')} requiredWhen={neededOnceBegun} autoComplete="off" />
      </div>
      <div className="console-hq-pair">
        <Text control={control} name="registeredAddress.postalCode" label={label('postalCode')} requiredWhen={neededOnceBegun} autoComplete="off" />
        <Controller
          control={control}
          name="registeredAddress.country"
          render={({ field, fieldState }) => (
            <SelectField label={label('country')} options={options} value={field.value} onValueChange={field.onChange} error={fieldState.error?.message} />
          )}
        />
      </div>
    </fieldset>
  );
}

/**
 * A kit `TextField` on one of the form's text values. `required` ones say so when left empty; a
 * `requiredWhen` one only while its condition holds (an address that has been begun).
 */
function Text({
  control,
  name,
  label,
  description,
  required = false,
  requiredWhen,
  onValue,
  ...input
}: {
  readonly control: Control<NewOrganizationValues>;
  readonly name: FormPath;
  readonly label: string;
  readonly description?: string;
  readonly required?: boolean;
  readonly requiredWhen?: () => boolean;
  readonly onValue?: (value: string) => void;
  readonly spellCheck?: boolean;
  readonly autoComplete?: string;
  readonly autoCapitalize?: string;
}) {
  const { t } = useTranslation('hq');
  const needed = () => required || (requiredWhen?.() ?? false);
  return (
    <Controller
      control={control}
      name={name}
      rules={{ validate: (value) => !needed() || String(value ?? '').trim() !== '' || t('organizations.new.required') }}
      render={({ field, fieldState }) => (
        <TextField
          {...input}
          className="console-hq-field"
          label={label}
          {...(description === undefined ? {} : { description })}
          required={required}
          showOptional={!required && requiredWhen === undefined}
          name={field.name}
          value={field.value as string}
          onChange={(event) => {
            field.onChange(event);
            onValue?.(event.target.value);
          }}
          onBlur={field.onBlur}
          error={fieldState.error?.message}
        />
      )}
    />
  );
}
